import { useState } from "react";
import { useDialogKeyboard } from '../../../hooks/useDialogKeyboard';
import {
  Box,
  Button,
  Modal,
  ModalClose,
  Sheet,
  Typography,
} from "@mui/joy";
import WarningIcon from "@mui/icons-material/Warning";

const DeleteUserModal = ({ open, onClose, user, onConfirmDelete, currentUserId }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const isSelfDelete = user?.id === currentUserId;

  const handleDelete = async () => {
    if (!user || isSelfDelete) return;

    setIsDeleting(true);
    const result = await onConfirmDelete(user.id);
    setIsDeleting(false);

    if (result.success) {
      onClose();
    }
  };

  useDialogKeyboard(open, { onEnter: handleDelete, onEscape: onClose, disabled: isDeleting || isSelfDelete });

  if (!user) return null;

  return (
    <Modal
      aria-labelledby="delete-user-modal-title"
      open={open}
      onClose={onClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          maxWidth: 400,
          width: "100%",
          borderRadius: "md",
          p: 3,
          boxShadow: "lg",
          bgcolor: "background.surface",
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} onClick={onClose} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <WarningIcon color="warning" />
          <Typography
            component="h2"
            id="delete-user-modal-title"
            level="h4"
            textColor="inherit"
            sx={{ fontWeight: "lg" }}
          >
            Delete User
          </Typography>
        </Box>

        <Typography textColor="text.secondary" sx={{ mb: 2 }}>
          Are you sure you want to delete the user{" "}
          <strong>{user.username}</strong>?
        </Typography>

        {isSelfDelete && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "danger.softBg",
              borderRadius: 1,
            }}
          >
            <Typography level="body-sm" color="danger">
              You cannot delete your own account. Please contact another
              administrator.
            </Typography>
          </Box>
        )}

        {user.role === "admin" && !isSelfDelete && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "warning.softBg",
              borderRadius: 1,
            }}
          >
            <Typography level="body-sm" color="warning">
              This user is an administrator. Deleting them may affect system
              management capabilities.
            </Typography>
          </Box>
        )}

        <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 3 }}>
          This action cannot be undone. All data associated with this user will
          be permanently removed.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onClose} variant="plain">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="solid"
            color="danger"
            loading={isDeleting}
            disabled={isSelfDelete}
          >
            Delete User
          </Button>
        </Box>
      </Sheet>
    </Modal>
  );
};

export default DeleteUserModal;
